import classNames from 'classnames'
import {
  any,
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOf,
  oneOfType,
  string,
} from 'prop-types'
import { PureComponent } from 'react'
import { kuiperClassNames } from '../../../utils/kuiperClassNames'
import Tooltip from './react-tippy/component'

const stripKuiperClassPrefixes = (cl) => {
  const regex = new RegExp('^(kuiper-|tracking-)', 'g')
  return cl
    .split(/\s/)
    .map((cl) => cl.replace(regex, ''))
    .join(' ')
}

export default class TippyAdapter extends PureComponent {
  static propTypes = {
    align: oneOf(['start', 'end']),
    animation: oneOfType([string, bool]),
    className: string,
    classNameObj: object,
    content: any.isRequired,
    disabled: bool,
    edgeOffset: number,
    flip: bool,
    flipBehavior: arrayOf(string),
    forceClose: bool,
    hasCaret: bool,
    inline: bool,
    interactive: bool,
    maxWidth: oneOfType([number, string]),
    onClose: func,
    onClosed: func,
    onMouseEnter: func,
    onOpen: func,
    onOpened: func,
    open: bool,
    popoverHandlers: object,
    position: string,
    showOnCreate: bool,
    sticky: oneOfType([bool, string]),
    style: object,
    theme: string,
    trigger: node.isRequired,
    triggerEvent: string,
    type: oneOf(['dropdown', 'lapidary_dropdown', 'popover', 'tooltip']),
  }

  static defaultProps = {
    animation: 'fade',
    edgeOffset: 10,
    inline: false,
    interactive: false,
    popoverHandlers: {},
  }

  // The 'theme' prop is no longer set as a classname, instead Tippy sets
  // it on the 'data-theme' attribute as of v6. We need to strip
  // kuiper- tags here from the given classnames
  get theme() {
    const { className, position, theme, type, classNameObj } = this.props

    return stripKuiperClassPrefixes(
      classNames([
        'popover-base',
        className,
        position,
        theme,
        type,
        classNameObj,
      ])
    )
  }

  get position() {
    const { align, position = 'auto' } = this.props

    if (!align) {
      return position
    }

    return `${position}-${align}`
  }

  render() {
    const {
      animation,
      className,
      content,
      disabled,
      edgeOffset,
      flip,
      flipBehavior,
      forceClose,
      hasCaret,
      inline,
      interactive,
      maxWidth,
      onClose,
      onClosed,
      onMouseEnter,
      onOpen,
      onOpened,
      open,
      popoverHandlers,
      showOnCreate,
      sticky,
      style,
      trigger,
      triggerEvent,
      type,
    } = this.props
    return (
      <Tooltip
        animation={animation}
        arrow={hasCaret}
        className={kuiperClassNames('popover-base', className)}
        content={content}
        disabled={disabled}
        flip={flip}
        flipBehavior={flipBehavior}
        forceClose={forceClose}
        inline={inline}
        interactive={interactive}
        edgeOffset={edgeOffset}
        maxWidth={maxWidth}
        onHidden={onClosed}
        onHide={onClose}
        onMouseEnter={onMouseEnter}
        onShow={onOpen}
        onShown={onOpened}
        open={open}
        placement={this.position}
        showOnCreate={showOnCreate}
        sticky={sticky}
        style={style}
        theme={this.theme}
        // our test cases leave triggerEvent undefined, it's unclear if any other uses of TippyAdapter do the same:
        trigger={triggerEvent && triggerEvent.replace('hover', 'mouseenter')}
        type={type}
        {...popoverHandlers}
      >
        {trigger}
      </Tooltip>
    )
  }
}
TippyAdapter.displayName = 'Kuiper: TippyAdapter ← PopoverBase ← PopoverV2'
